import { ImageLoaderManager } from '@/infrastructure/engine2d/assets/ImageLoaderManager';
import { getFabricGroupById } from '@/infrastructure/engine2d/helpers/FabricGroupHelper';
import { GRID_HELPER_ID } from '@/domain/selection/reservedSelectionIds';
import { WORKING_ZONE_NAME } from '@/infrastructure/engine2d/fabricGroupsName';
import { FabricImage, Group, type Canvas } from 'fabric';

type TGridOverlayServiceParams = {
    canvas: Canvas;
    getImageRenderHeight: () => number;
    getImageRenderWidth: () => number;
};

// Behavior-preserving extraction of the grid-overlay logic previously
// embedded in FabricScene (createGridHelper, setGridHelperVisibility,
// deleteGridHelper, keepGridImageUpFront). Injected with its slice of
// the world (canvas + render-size getters) — no back-reference to the
// World, per the Injection > Extension rule.
class GridOverlayService {
    private readonly _canvas: Canvas;
    private readonly _getImageRenderHeight: () => number;
    private readonly _getImageRenderWidth: () => number;

    constructor(params: TGridOverlayServiceParams) {
        this._canvas = params.canvas;
        this._getImageRenderHeight = params.getImageRenderHeight;
        this._getImageRenderWidth = params.getImageRenderWidth;
    }

    async create(gridUrl: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            ImageLoaderManager.loadImage(gridUrl)
                .then((imageLoaded) => {
                    if (imageLoaded instanceof Error) {
                        console.error('Error loading image:', imageLoaded);
                        return;
                    }
                    const oldGridGroup = getFabricGroupById(this._canvas, GRID_HELPER_ID);
                    if (oldGridGroup) {
                        this._canvas.remove(oldGridGroup);
                    }
                    const foreGroundGroup = new Group([], {
                        evented: false,
                        lockMovementX: true,
                        lockMovementY: true,
                        lockRotation: true,
                        lockScalingX: true,
                        lockScalingY: true,
                        selectable: false,
                        visible: false,
                    });

                    foreGroundGroup.set('id', GRID_HELPER_ID);
                    const gridImage = new FabricImage(imageLoaded, {
                        evented: false,
                        id: 'gridHelper',
                        imageSmoothing: true,
                        left: this._getImageRenderWidth() / 2,
                        lockMovementX: true,
                        lockMovementY: true,
                        lockRotation: true,
                        lockScalingX: true,
                        lockScalingY: true,
                        opacity: 0.5,
                        originX: 'center',
                        originY: 'center',
                        selectable: false,
                        top: this._getImageRenderHeight() / 2,
                    });
                    foreGroundGroup.add(gridImage);

                    const workingZone = this._canvas.getObjects().find((obj) => obj.get('name') === WORKING_ZONE_NAME);
                    if (workingZone) {
                        // place grid just above working zone
                        const workingZoneIndex = this._canvas.getObjects().indexOf(workingZone);
                        this._canvas.insertAt(workingZoneIndex + 1, foreGroundGroup);
                    } else {
                        // add grid at the bottom of the stack
                        this._canvas.add(foreGroundGroup);
                        this._canvas.sendObjectToBack(foreGroundGroup);
                    }

                    this._canvas.renderAll();
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    keepUpFront(): void {
        const gridImage = this._canvas.getObjects().find((obj) => (obj as { id?: string }).id === GRID_HELPER_ID);
        if (gridImage) {
            this._canvas.bringObjectToFront(gridImage);
            this._canvas.renderAll();
        }
    }

    remove(): void {
        const gridImage = this._canvas.getObjects().find((obj) => (obj as { id?: string }).id === GRID_HELPER_ID);
        if (!gridImage) {
            return;
        }
        gridImage.hasControls = false;
        this._canvas.renderAll();
        this._canvas.remove(gridImage);
        gridImage.dispose();
    }

    setVisibility(isVisible: boolean): void {
        const gridGroupHelper = getFabricGroupById(this._canvas, GRID_HELPER_ID);
        if (!gridGroupHelper) {
            return;
        }
        gridGroupHelper.set('visible', isVisible);
        this._canvas.renderAll();
    }
}

export { GridOverlayService };
